import { useMemo } from 'react';
import { PROFILE_CONSTANTS } from '../../va-profile-settings/constants/profileConstants';

/**
 * Custom hook to calculate profile completion percentage
 * @param {Object} profile - The user profile data
 * @returns {number} - Profile completion percentage (0-100)
 */
export const useProfileCompletion = (profile) => {
  return useMemo(() => {
    if (!profile) return 0;
    
    const requiredFields = PROFILE_CONSTANTS.REQUIRED_FIELDS;
    const optionalFields = PROFILE_CONSTANTS.OPTIONAL_FIELDS;
    const requiredWeight = PROFILE_CONSTANTS.COMPLETION_WEIGHTS.REQUIRED_WEIGHT;
    const optionalWeight = PROFILE_CONSTANTS.COMPLETION_WEIGHTS.OPTIONAL_WEIGHT;
    
    let requiredCompleted = 0;
    let optionalCompleted = 0;
    
    // Check required fields
    requiredFields.forEach(field => {
      if (hasValue(profile[field])) {
        requiredCompleted++;
      }
    });
    
    // Check optional fields
    optionalFields.forEach(field => {
      if (hasValue(profile[field])) {
        optionalCompleted++;
      }
    });

    // Special handling for portfolio items (from separate table)
    if (profile.portfolio_items && Array.isArray(profile.portfolio_items) && profile.portfolio_items.length > 0) {
      optionalCompleted += 1; // Add bonus point for having portfolio items
    }

    // Special handling for profile picture
    if (profile.profile_picture && profile.profile_picture.trim() !== '') {
      optionalCompleted += 1; // Add bonus point for profile picture
    }
    
    const requiredPercentage = (requiredCompleted / requiredFields.length) * requiredWeight;
    const adjustedOptionalFields = optionalFields.length + 2; // Account for portfolio_items and profile_picture
    const optionalPercentage = (optionalCompleted / adjustedOptionalFields) * optionalWeight;
    
    return Math.round((requiredPercentage + optionalPercentage) * 100);
  }, [profile]);
};

/**
 * Helper to check if a field has a value
 * @param {any} value - The value to check
 * @returns {boolean} - Whether the value is considered to have content
 */
const hasValue = (value) => {
  if (value === undefined || value === null || value === '') {
    return false;
  }
  
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  
  if (typeof value === 'object') {
    return Object.keys(value).length > 0;
  }
  
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  
  return true;
};

export default useProfileCompletion;
